<aw-section [testIdAffix]="'account-orders-section'">
  @let name = ticketName();
  @if (isLoading()) {
    <aw-loading-animation></aw-loading-animation>
  } @else {
    <div
      class="text-3xl font-semibold"
      [attr.data-testid]="'orders-page-headline'">
      {{
        texts()
          | filterTexts: ERelationType.headline : EEntityType.directTicket
          | language
      }}
    </div>
    <aw-text-input
      id="account-orders-search"
      [label]="
        texts()
          | filterTexts: ERelationType.search : EEntityType.generic
          | language
      "
      [formControl]="searchControl"></aw-text-input>
    <div class="flex flex-row gap-4 text-sm">
      @for (filter of filterOptions; track $index) {
        <div
          [attr.data-testid]="'order-filter-time-frame-' + filter"
          tabindex="0"
          class="cursor-pointer border-1 px-2 py-1"
          [ngStyle]="{
            borderColor: corporateDesign.primaryColor(),
            color: corporateDesign.primaryColor(),
            background:
              filterControl.value === filter
                ? corporateDesign.primaryColor() + '1A'
                : '',
            borderRadius: corporateDesign.borderRadius(),
          }"
          (keydown.enter)="changeFilter(filter)"
          (click)="changeFilter(filter)">
          {{
            texts() | filterTexts: filter : EEntityType.directTicket | language
          }}
        </div>
      }
    </div>
    <div
      class="text-xl font-semibold"
      [attr.data-testid]="'current-time-frame-filter'">
      {{
        texts()
          | filterTexts: filterControl.value : EEntityType.directTicket
          | language
      }}
    </div>
    <div class="flex flex-col gap-4">
      @for (order of shownOrders(); track $index) {
        <aw-section
          [reverseHeader]="true"
          [testIdAffix]="'account-orders-list'"
          [isCollapsible]="true">
          <aw-section-header>
            @let orderTestId = 'order-' + $index;
            <div class="min-h-30 w-full" [attr.data-testid]="orderTestId">
              <div class="relative w-full pb-4">
                <div
                  class="pr-[126px] text-xl font-semibold break-all"
                  [attr.data-testid]="'order-headline-' + orderTestId">
                  {{ nameForOrder(order) }}
                </div>
                <div class="absolute top-0 right-5 flex flex-row gap-4">
                  <div
                    class="text-xl font-semibold"
                    [attr.data-testid]="'order-total-price-' + orderTestId">
                    {{ totalPrice(order.bookings) }}
                  </div>
                  <div class="right-0 flex cursor-pointer flex-col gap-4">
                    <div class="absolute top-0">
                      <span
                        tabindex="0"
                        class="material-symbols-rounded"
                        [attr.data-testid]="
                          'order-history-open-sub-menu' | appendTestId: name
                        "
                        (keydown.enter)="toggleSubMenu(order.id)"
                        (click)="toggleSubMenu(order.id)"
                        >more_vert</span
                      >

                      @if (openedProductId() === order.id) {
                        <aw-order-history-sub-menu
                          [showResend]="!order.onDemand"
                          (onSubmenuClick)="handleSubMenuClick($event, order)">
                        </aw-order-history-sub-menu>
                      }
                    </div>
                  </div>
                </div>
              </div>
              <ng-container
                *ngTemplateOutlet="
                  orderDetails;
                  context: {
                    orderTestId,
                    createdAt: order.createdAt,
                    orderId: order.id,
                    payment: order.paymentMethod,
                    events: order.events,
                  }
                "></ng-container>
              @if (shouldShowOnDemandCancellationDeadline(order)) {
                <div class="mt-3 text-sm">
                  <span class="font-semibold"
                    >{{
                      texts()
                        | filterTexts
                          : ERelationType.orderHistoryCancellableUntil
                          : EEntityType.generic
                        | language
                    }}:
                  </span>
                  {{
                    order.onDemand?.journey?.cancellableUntil
                      | date: 'dd.MM.yyyy, HH:mm'
                  }}
                </div>
              }
              @if (order.onDemand?.cancellationReason) {
                <div class="mt-3 text-sm">
                  <span class="font-semibold"
                    >{{
                      texts()
                        | filterTexts
                          : ERelationType.orderHistoryCancellationReasonLabel
                          : EEntityType.generic
                        | language
                    }}:
                  </span>
                  {{ order.onDemand?.cancellationReason }}
                </div>
              }
            </div>
          </aw-section-header>

          @if (order && order.bookings) {
            @let numberOfTickets = order.bookings.length || 0;
            @if (numberOfTickets > 0) {
              <ng-container *ngTemplateOutlet="divider"></ng-container>
            }
            @for (booking of order.bookings; track $index) {
              <ng-container
                *ngTemplateOutlet="
                  ticketDetails;
                  context: {
                    purchase: order.purchase?.articles?.[$index],
                    ticketTestId: orderTestId + '-' + $index,
                    ticket: booking,
                    events: order.events,
                    onDemandItems: order.onDemand?.items,
                  }
                "></ng-container>
              @if ($index < numberOfTickets - 1) {
                <ng-container *ngTemplateOutlet="divider"></ng-container>
              }
            }
          }
        </aw-section>
      }
    </div>
  }
</aw-section>
<ng-template #divider>
  <div class="w-full border-t-1"></div>
</ng-template>

<ng-template
  #orderDetails
  let-orderTestId="orderTestId"
  let-createdAt="createdAt"
  let-orderId="orderId"
  let-events="events"
  let-payment="payment">
  <div class="flex w-full md:pr-[60px]">
    <div class="flex w-full flex-row flex-wrap gap-6">
      <div>
        <div
          class="text-sm font-semibold"
          [attr.data-testid]="'order-date-label-' + orderTestId">
          {{
            texts()
              | filterTexts: ERelationType.orderDate : EEntityType.directTicket
              | language
          }}
        </div>
        <div class="text-sm" [attr.data-testid]="'order-date-' + orderTestId">
          {{
            createdAt
              ? (createdAt | date: dateFormatSupport.activeDateFormat())
              : '-'
          }}
        </div>
      </div>
      <div>
        <div
          class="text-sm font-semibold"
          [attr.data-testid]="'order-status-label-' + orderTestId">
          {{
            texts()
              | filterTexts
                : ERelationType.orderStatus
                : EEntityType.directTicket
              | language
          }}
        </div>
        <aw-order-status-chip
          [id]="orderTestId"
          [orderEvents]="events"></aw-order-status-chip>
      </div>
      <div>
        <div
          class="text-sm font-semibold"
          [attr.data-testid]="'order-id-label-' + orderTestId">
          {{
            texts()
              | filterTexts
                : ERelationType.orderNumber
                : EEntityType.directTicket
              | language
          }}
        </div>
        <div class="text-sm" [attr.data-testid]="'order-id-' + orderTestId">
          {{ orderId }}
        </div>
      </div>
      <div>
        <div
          class="text-sm font-semibold"
          [attr.data-testid]="'order-payment-label-' + orderTestId">
          {{
            texts()
              | filterTexts
                : ERelationType.paymentType
                : EEntityType.directTicket
              | language
          }}
        </div>
        <div
          class="text-sm"
          [attr.data-testid]="'order-payment-' + orderTestId">
          {{ resolvePaymentMethod(payment) }}
        </div>
      </div>
    </div>
  </div>
</ng-template>

<ng-template
  #ticketDetails
  let-ticketTestId="ticketTestId"
  let-ticket="ticket"
  let-purchase="purchase"
  let-events="events"
  let-onDemandItems="onDemandItems">
  @let product = ticket.productDefinition;
  <div class="flex w-full">
    <div class="min-w-[60px]"></div>
    <div class="flex w-full flex-row flex-wrap gap-6">
      <div>
        <div
          class="text-sm font-semibold"
          [attr.data-testid]="'ticket-date-label' + ticketTestId">
          {{
            texts()
              | filterTexts: ERelationType.travelData : EEntityType.directTicket
              | language
          }}
        </div>
        <div class="text-sm" [attr.data-testid]="'ticket-date-' + ticketTestId">
          {{
            ticket.options.travelDate
              | date: dateFormatSupport.activeDateFormat()
          }}
        </div>
      </div>

      <div>
        <div
          class="text-sm font-semibold"
          [attr.data-testid]="'ticket-status-label-' + ticketTestId">
          {{
            texts()
              | filterTexts: ERelationType.status : EEntityType.directTicket
              | language
          }}
        </div>
        <aw-order-status-chip
          [id]="ticketTestId"
          [type]="EStatusChipType.label"
          [orderEvents]="events"></aw-order-status-chip>
      </div>

      <div>
        <div
          class="text-sm font-semibold"
          [attr.data-testid]="'ticket-info-label-' + ticketTestId">
          {{
            texts()
              | filterTexts: ERelationType.info : EEntityType.directTicket
              | language
          }}
        </div>
        <div
          class="flex flex-col text-sm"
          [attr.data-testid]="'ticket-info-' + ticketTestId">
          @if (onDemandItems?.length) {
            @for (item of onDemandItems; track $index) {
              <span
                [attr.data-testid]="
                  'ticket-info-element-' + $index + '-' + ticketTestId
                "
                >Name: {{ item.product }}</span
              >
            }
          } @else {
            <span [attr.data-testid]="'ticket-info-class-' + ticketTestId">{{
              product.comfortClass
            }}</span>
            @for (element of product.keyElements; track $index) {
              <span
                [attr.data-testid]="
                  'ticket-info-element-' + $index + '-' + ticketTestId
                "
                >{{ element.name }}: {{ element.value }}</span
              >
            }
          }
        </div>
      </div>

      <div>
        <div
          class="text-sm font-semibold"
          [attr.data-testid]="'ticket-price-label-' + ticketTestId">
          {{
            texts()
              | filterTexts: ERelationType.price : EEntityType.directTicket
              | language
          }}
        </div>
        <div
          class="text-sm"
          [attr.data-testid]="'ticket-price-' + ticketTestId">
          {{
            purchase?.totalSum ?? product.price.priceIncludingVat
              | decimalsToCents
              | centsToEuros
          }}
        </div>
      </div>
    </div>
    <div class="min-w-[60px]"></div>
  </div>
</ng-template>
